/**
 * Typed coordinates for the airport and refuel points of a plan (Diurnal sign-off 04-032, MB decision 346; inventory
 * 04 §7.1, the Diurnal map's `fpParseCoordinateString` am:16887 and `latLonToUtm` am:16733).
 *
 * One box takes any of the forms crews copy from strip guides, ERSA, Google Earth or a GPS:
 * - UTM: `55K 493207.58 7584040.44`, `Zone 55S 493207 7584040`, `55 493207 7584040 S`. The letter after the zone is
 *   read as the Diurnal tool reads it: `S` is south and `N` is north (as Australian crews write the hemisphere); any
 *   other letter is a latitude band, C to M south and P to X north. (MGRS band S is in the northern hemisphere, so a
 *   band-letter `S` from an MGRS reference is misread; that is the Diurnal tool's rule, kept on purpose.)
 * - Decimal degrees: `-21.38524, 147.33434`, `21.38524 S 147.33434 E`, `S21.38524 E147.33434`.
 * - Degrees and decimal minutes: `21°23.114'S 147°20.061'E`, `21 23.114 S, 147 20.061 E`.
 * - Degrees, minutes and seconds: `21°23'06.9"S 147°20'03.6"E`, `-21 23 06.9, 147 20 03.6`.
 *
 * Degree, minute and second marks (straight or curly, `º` too) are read as spaces. Unlike the Diurnal parser,
 * minutes and seconds of 60 or more and UTM eastings or northings out of range are refused with the reason, never
 * read as another place.
 */
import type { LatLng } from './planning.js';
import { utmToLatLng } from './map-geo.js';

export type CoordinateFormat = 'utm' | 'decimal' | 'ddm' | 'dms';

export type ParsedCoordinates =
  | { readonly ok: true; readonly at: LatLng; readonly format: CoordinateFormat }
  | { readonly ok: false; readonly error: string };

/** What the box accepts, for its hint and the error message. */
export const COORDINATE_HINT =
  'UTM (55K 493207 7584040), decimal degrees (-21.38524, 147.33434), degrees and minutes (21 23.114 S 147 20.061 E) or degrees, minutes and seconds (21°23\'06.9"S 147°20\'03.6"E)';

const fail = (error: string): ParsedCoordinates => ({ ok: false, error });

/** The hemisphere of a UTM zone letter: `S` and `N` as written in Australia, else the band (C–M south). */
export function utmLetterHemisphere(letter: string): 'N' | 'S' | null {
  const l = letter.toUpperCase();
  if (l === 'S') return 'S';
  if (l === 'N') return 'N';
  if (/^[C-HJ-M]$/.test(l)) return 'S';
  if (/^[P-X]$/.test(l) && l !== 'S') return 'N';
  return null;
}

function fromUtm(zone: number, hemi: 'N' | 'S', easting: number, northing: number): ParsedCoordinates {
  if (!Number.isInteger(zone) || zone < 1 || zone > 60)
    return fail(`UTM zone ${zone} is not a zone (1 to 60)`);
  if (!(easting >= 100000 && easting <= 900000)) {
    return fail(`Easting ${easting} m is out of range for UTM (100000 to 900000 m)`);
  }
  if (!(northing >= 0 && northing <= 10000000)) {
    return fail(`Northing ${northing} m is out of range for UTM (0 to 10000000 m)`);
  }
  return { ok: true, at: utmToLatLng(easting, northing, zone, hemi), format: 'utm' };
}

/** Degrees from 1 to 3 parts (d, d m, d m s); null when a minute or second part is 60 or more. */
function fromParts(parts: readonly number[]): number | null {
  if (parts.length === 0 || parts.length > 3 || parts.some((p) => !Number.isFinite(p) || p < 0)) return null;
  const [d, m = 0, s = 0] = parts;
  if (m >= 60 || s >= 60) return null;
  if (parts.length > 1 && !Number.isInteger(d!)) return null;
  if (parts.length > 2 && !Number.isInteger(m)) return null;
  return d! + m / 60 + s / 3600;
}

const formatOf = (n: number): CoordinateFormat => (n === 1 ? 'decimal' : n === 2 ? 'ddm' : 'dms');

const nums = (s: string): number[] =>
  s
    .trim()
    .split(/\s+/)
    .filter((x) => x !== '')
    .map(Number);

function checked(lat: number, lng: number, format: CoordinateFormat): ParsedCoordinates {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return fail(`Not coordinates. Type ${COORDINATE_HINT}`);
  if (Math.abs(lat) > 90) return fail(`Latitude ${lat.toFixed(5)}° is beyond 90°`);
  if (Math.abs(lng) > 180) return fail(`Longitude ${lng.toFixed(5)}° is beyond 180°`);
  return { ok: true, at: { lat, lng }, format };
}

/** A latitude and longitude, each with its hemisphere letter (N/S and E/W, in either order). */
function lettered(
  a: { v: number; h: string },
  b: { v: number; h: string },
  n: number,
): ParsedCoordinates | null {
  const isLat = (h: string): boolean => /^[NS]$/i.test(h);
  const [la, lo] = isLat(a.h) && !isLat(b.h) ? [a, b] : !isLat(a.h) && isLat(b.h) ? [b, a] : [null, null];
  if (!la || !lo) return null;
  const lat = /^S$/i.test(la.h) ? -la.v : la.v;
  const lng = /^W$/i.test(lo.h) ? -lo.v : lo.v;
  return checked(lat, lng, formatOf(n));
}

/** Read a typed position. */
export function parseCoordinates(text: string): ParsedCoordinates {
  const s = text.trim();
  if (s === '') return fail('Type a position');

  // UTM with a zone letter: "55K 493207 7584040", "Zone 55S 493207.58, 7584040.44"
  const utmA = /^(?:zone\s*)?(\d{1,2})\s*([A-Za-z])\s+(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)$/i.exec(s);
  if (utmA) {
    const hemi = utmLetterHemisphere(utmA[2]!);
    if (!hemi) return fail(`"${utmA[2]}" is not a UTM latitude band or hemisphere (C to X, or N and S)`);
    return fromUtm(Number(utmA[1]), hemi, Number(utmA[3]), Number(utmA[4]));
  }
  // UTM with the hemisphere last: "55 493207 7584040 S"
  const utmB = /^(?:zone\s*)?(\d{1,2})\s+(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)\s*([NS])$/i.exec(s);
  if (utmB) {
    return fromUtm(Number(utmB[1]), utmB[4]!.toUpperCase() as 'N' | 'S', Number(utmB[2]), Number(utmB[3]));
  }

  const clean = s
    .replace(/[°˚º˚]/g, ' ')
    .replace(/[′'‘’ʼ]/g, ' ')
    .replace(/[″"“”]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const part = String.raw`\d+(?:\.\d+)?(?: \d+(?:\.\d+)?){0,2}`;

  // hemisphere letters after each half: "21 23 06.9 S 147 20 03.6 E", "21.38524 S, 147.33434 E"
  const trailing = new RegExp(`^(${part}) ?([NSEW]) ?,? ?(${part}) ?([NSEW])$`, 'i').exec(clean);
  // hemisphere letters before each half: "S21 23.114 E147 20.061"
  const leading = new RegExp(`^([NSEW]) ?(${part}) ?,? ?([NSEW]) ?(${part})$`, 'i').exec(clean);
  const halves = trailing
    ? { a: trailing[1]!, ha: trailing[2]!, b: trailing[3]!, hb: trailing[4]! }
    : leading
      ? { a: leading[2]!, ha: leading[1]!, b: leading[4]!, hb: leading[3]! }
      : null;
  if (halves) {
    const pa = nums(halves.a);
    const pb = nums(halves.b);
    if (pa.length !== pb.length) return fail('Give the latitude and the longitude in the same form');
    const va = fromParts(pa);
    const vb = fromParts(pb);
    if (va === null || vb === null) return fail('Minutes and seconds must be under 60');
    const r = lettered({ v: va, h: halves.ha }, { v: vb, h: halves.hb }, pa.length);
    if (r) return r;
    return fail('Give one latitude (N or S) and one longitude (E or W)');
  }

  // signed, with a comma between: "-21 23 06.9, 147 20 03.6", "-21.38524, 147.33434"
  const signed = new RegExp(`^(-?${part}) ?, ?(-?${part})$`).exec(clean);
  // two signed decimals with a space: "-21.38524 147.33434"
  const pair = /^(-?\d+(?:\.\d+)?) (-?\d+(?:\.\d+)?)$/.exec(clean);
  const sides = signed ? [signed[1]!, signed[2]!] : pair ? [pair[1]!, pair[2]!] : null;
  if (sides) {
    const read = (t: string): { v: number | null; n: number } => {
      const neg = t.startsWith('-');
      const p = nums(neg ? t.slice(1) : t);
      const v = fromParts(p);
      return { v: v === null ? null : neg ? -v : v, n: p.length };
    };
    const a = read(sides[0]!);
    const b = read(sides[1]!);
    if (a.n !== b.n) return fail('Give the latitude and the longitude in the same form');
    if (a.v === null || b.v === null) return fail('Minutes and seconds must be under 60');
    return checked(a.v, b.v, formatOf(a.n));
  }
  return fail(`Not coordinates. Type ${COORDINATE_HINT}`);
}

export interface UtmPosition {
  readonly easting: number;
  readonly northing: number;
  readonly zone: number;
  readonly hemisphere: 'N' | 'S';
}

/**
 * Latitude and longitude to UTM (WGS 84) in the point's own zone, or in `zone` when given (a block's zone), as the
 * Diurnal map's `latLonToUtm` (am:16733), the forward twin of `utmToLatLng`.
 */
export function latLngToUtm(at: LatLng, zone?: number): UtmPosition {
  const a = 6378137;
  const f = 1 / 298.257223563;
  const e = Math.sqrt(2 * f - f * f);
  const e2 = (e * e) / (1 - e * e);
  const k0 = 0.9996;
  const z = zone ?? Math.min(60, Math.floor((at.lng + 180) / 6) + 1);
  const lng0 = (((z - 1) * 6 - 180 + 3) * Math.PI) / 180;
  const latR = (at.lat * Math.PI) / 180;
  const lngR = (at.lng * Math.PI) / 180;
  const n = a / Math.sqrt(1 - e * e * Math.sin(latR) * Math.sin(latR));
  const t = Math.tan(latR) * Math.tan(latR);
  const c = e2 * Math.cos(latR) * Math.cos(latR);
  const aa = Math.cos(latR) * (lngR - lng0);
  const m =
    a *
    ((1 - (e * e) / 4 - (3 * e ** 4) / 64 - (5 * e ** 6) / 256) * latR -
      ((3 * e * e) / 8 + (3 * e ** 4) / 32 + (45 * e ** 6) / 1024) * Math.sin(2 * latR) +
      ((15 * e ** 4) / 256 + (45 * e ** 6) / 1024) * Math.sin(4 * latR) -
      ((35 * e ** 6) / 3072) * Math.sin(6 * latR));
  const easting =
    k0 * n * (aa + ((1 - t + c) * aa ** 3) / 6 + ((5 - 18 * t + t * t + 72 * c - 58 * e2) * aa ** 5) / 120) +
    500000;
  let northing =
    k0 *
    (m +
      n *
        Math.tan(latR) *
        ((aa * aa) / 2 +
          ((5 - t + 9 * c + 4 * c * c) * aa ** 4) / 24 +
          ((61 - 58 * t + t * t + 600 * c - 330 * e2) * aa ** 6) / 720));
  if (at.lat < 0) northing += 10000000;
  return { easting, northing, zone: z, hemisphere: at.lat >= 0 ? 'N' : 'S' };
}

/** "493207.58E 7584040.44N Zone 55S", as the Diurnal airport pin shows it. */
export function utmWords(u: UtmPosition): string {
  return `${u.easting.toFixed(2)}E ${u.northing.toFixed(2)}N Zone ${u.zone}${u.hemisphere}`;
}

/** "-21.38524, 147.33434" (5 decimals, about 1 m). */
export function decimalWords(at: LatLng): string {
  return `${at.lat.toFixed(5)}, ${at.lng.toFixed(5)}`;
}
